import {
  ContentHeader,
  Css,
  FilterDefs,
  GridSortConfig,
  GridTableLayout,
  HeaderAction,
  multiFilter,
  RowStyles,
  toggleFilter,
  useComputed,
  useGridTableApi,
  useGridTableLayoutState,
  useModal,
  useRightPane,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { baseDownloadUrl } from "src/context";
import {
  Maybe,
  ProductOfferingOrder,
  ProductOfferingsFilter,
  ProductOfferingVersionMode,
  ReadyPlanStatus,
} from "src/generated/graphqlTypes";
import { publishDisabledReason } from "src/routes/developments/product-offerings/components/usePublishProductOffering";
import {
  defaultGlobalProductOfferingsFilter,
  defaultGlobalProductOfferingsOrder,
  defaultGlobalProductOfferingsPage,
} from "src/routes/libraries/product-offerings/GlobalProductOfferings";
import { BulkPublishProductOfferingsModal } from "src/routes/libraries/product-offerings/overview/BulkPublishProductOfferingsModal";
import { productOfferingError } from "src/routes/libraries/product-offerings/overview/ProductOfferingErrorBanner";
import { ProductOfferingOverviewPanel } from "src/routes/libraries/product-offerings/overview/ProductOfferingOverviewPanel";
import {
  ProductOfferingsOverviewPageQuery,
  useProductOfferingsOverview_SaveProductOfferingMutation,
  useProductOfferingsOverviewPageQuery,
} from "src/routes/libraries/product-offerings/overview/ProductOfferingsOverviewTab.generated";
import {
  createProductOfferingsOverviewColumns,
  createProductOfferingsOverviewRows,
  ProductOfferingsOverviewTableRow,
  productOfferingStatusTag,
} from "src/routes/libraries/product-offerings/overview/ProductOfferingsOverviewTable";
import { ProductOfferingStatusSummary } from "src/routes/libraries/product-offerings/overview/ProductOfferingStatusSummary";
import { addEntityParam, createProductOfferingConfigsUrl, createProductOfferingUrl } from "src/RouteUrls";
import { parseOrder, toOrder } from "src/utils/ordering";
import { openInSelf } from "src/utils/window";

type ProductOfferingsOverviewTabProps = { projectId: string };

export function ProductOfferingsOverviewTab(props: ProductOfferingsOverviewTabProps) {
  const { projectId } = props;
  const navigate = useNavigate();
  // TODO: These are stubbed filters. The actual filters will be added in: https://linear.app/homebound-team/issue/PLAN-2008/febe-implement-overview-table-filters
  // It might require a BE change to support the filters, tbd, so I'm splitting this into a separate ticket
  const filterDefs: FilterDefs<ProductOfferingsOverviewFilter> = useMemo(
    () => ({
      status: multiFilter({
        label: "POF Status",
        options: [ReadyPlanStatus.Active, ReadyPlanStatus.Draft].map((code) => ({
          code,
          name: productOfferingStatusTag[code].text,
        })),
        getOptionValue: (o) => o.code,
        getOptionLabel: (o) => o.name,
      }),
      archived: toggleFilter({ label: "Show Archived" }),
    }),
    [],
  );
  const layoutState = useGridTableLayoutState<ProductOfferingsOverviewFilter>({
    persistedFilter: { filterDefs, storageKey: "project-product-offerings-overview-filter" },
    search: "server",
  });
  const [orderBy, setOrderBy] = useState<ProductOfferingOrder>(defaultGlobalProductOfferingsOrder);
  const [selectedPofId, setSelectedPofId] = useState<string>();
  const { openRightPane, isRightPaneOpen } = useRightPane();
  const { openModal } = useModal();
  const tableApi = useGridTableApi<ProductOfferingsOverviewTableRow>();
  const query = useProductOfferingsOverviewPageQuery({
    variables: {
      filter: mapToFilter(layoutState.filter, layoutState.searchString, projectId),
      orderBy,
      page: defaultGlobalProductOfferingsPage,
    },
  });
  const [saveProductOffering] = useProductOfferingsOverview_SaveProductOfferingMutation();
  const selected = useComputed(() => tableApi.getSelectedRows("productOffering").map((row) => row.data), [tableApi]);
  // With no rows selected, Publish publishes every loaded offering that has a publishable draft (only the 1st page
  // until more rows are scrolled into view)
  const toPublish = selected.nonEmpty
    ? selected
    : (query.data?.productOfferings.entities ?? []).filter((po) => !publishDisabledReason(po));
  const sorting: GridSortConfig = useMemo(
    () => ({
      on: "server",
      onSort: (key, direction) => setOrderBy(toOrder(key, direction)),
      value: parseOrder(orderBy),
    }),
    [orderBy],
  );
  const columns = useMemo(
    () =>
      createProductOfferingsOverviewColumns({
        projectId,
        onToggle: (id, input) => saveProductOffering({ variables: { input: { id, ...input } } }),
      }),
    [projectId, saveProductOffering],
  );
  const rowStyles: RowStyles<ProductOfferingsOverviewTableRow> = useMemo(
    () => ({
      productOffering: {
        onClick: ({ id, data }) => {
          setSelectedPofId(id);
          openRightPane({
            content: (
              <ProductOfferingOverviewPanel key={id} productOfferingId={id} versionId={data.aggregateLatest.id} />
            ),
          });
        },
        // Beam also applies cellCss to the companion row, so the row and its error banners share the tint
        cellCss: ({ data }) => (productOfferingError(data) ? Css.bgRed50.$ : {}),
      },
    }),
    [openRightPane],
  );
  const createRows = useCallback(
    (data: ProductOfferingsOverviewPageQuery | undefined) =>
      createProductOfferingsOverviewRows(data?.productOfferings.entities ?? [], projectId),
    [projectId],
  );
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.productOfferings.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: {
          page: { offset: query.data.productOfferings.entities.length, limit: defaultGlobalProductOfferingsPage.limit },
        },
      });
    }
  }, [query]);
  const actions: HeaderAction[] = [
    {
      label: "Create",
      variant: "secondary",
      onClick: () => navigate(createProductOfferingUrl(addEntityParam, undefined, undefined, projectId)),
    },
    {
      label: "Publish",
      variant: "primary",
      disabled: toPublish.isEmpty && "There are no drafts to publish",
      onClick: () =>
        openModal({
          size: "xl",
          content: (
            <BulkPublishProductOfferingsModal
              productOfferings={toPublish}
              // Deselect before the publish result updates the cache: once a published row is `selectable: false`,
              // Beam's `clearSelections` skips it and the row stays checked
              // TODO: Fix this in beam https://linear.app/homebound-team/issue/PLAN-2031/beam-let-clearselections-deselect-rows-that-became-unselectable
              onPublish={() => tableApi.clearSelections()}
            />
          ),
        }),
    },
    // Same actions as the v1 project list
    {
      kind: "menu",
      trigger: { icon: "verticalDots", variant: "outline" },
      tooltip: "Actions",
      items: [
        { label: "Compare Configurations", onClick: createProductOfferingConfigsUrl(undefined, projectId) },
        {
          label: "Download Plan Package Diff",
          onClick: () => {
            const params = new URLSearchParams({ type: "planPackageScopeDiff", projectId });
            openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
          },
        },
      ],
    },
  ];

  return (
    <>
      <ContentHeader title="Overview" actions={actions} />
      <ProductOfferingStatusSummary projectId={projectId} />
      <GridTableLayout
        layoutState={layoutState}
        emptyFallback="No Product Offerings Found"
        withRightPane={380}
        tableProps={{
          query,
          createRows,
          columns,
          sorting,
          rowStyles,
          api: tableApi,
          as: "virtual",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
          activeRowId: isRightPaneOpen && selectedPofId ? `productOffering_${selectedPofId}` : undefined,
        }}
      />
    </>
  );
}

type ProductOfferingsOverviewFilter = Pick<ProductOfferingsFilter, "status"> & { archived?: Maybe<boolean> };

function mapToFilter(
  filter: ProductOfferingsOverviewFilter,
  searchString: string | undefined,
  projectId: string,
): ProductOfferingsFilter {
  const { status, archived } = filter;
  return {
    ...(status?.nonEmpty ? { effectiveStatus: status } : archived ? {} : defaultGlobalProductOfferingsFilter),
    ...(searchString && { search: searchString }),
    project: [projectId],
    versionMode: ProductOfferingVersionMode.Latest,
  };
}
